import {
  normalModel,
  empiricalModel,
  logNormalModel,
  weibullModel,
  robustModel
} from './statisticalModels';
import { OTR_THRESHOLDS } from '../features/Reportable/utils/OTRAnalytics';

/**
 * Process stop data to calculate proposed times and model results
 * @param {Object} reportData - The report data containing proposed timetables
 * @returns {Array} Processed stop data with model results
 */
export const processStopData = (reportData) => {
  if (!reportData || !reportData.proposedTimetables || !reportData.proposedTimetables.proposedTimetables) return [];
  
  // Group stops by tripId for optimization
  const stopsByTrip = {};
  reportData.proposedTimetables.proposedTimetables.forEach(stop => {
    if (!stopsByTrip[stop.tripId]) stopsByTrip[stop.tripId] = [];
    stopsByTrip[stop.tripId].push(stop);
  });

  const result = [];
  Object.values(stopsByTrip).forEach(stops => {
    // Sort stops by scheduledTime
    stops.sort((a, b) => {
      const tA = a.scheduledTime.split(' ')[1] || a.scheduledTime;
      const tB = b.scheduledTime.split(' ')[1] || b.scheduledTime;
      return tA.localeCompare(tB);
    });

    // First pass: Calculate initial model results and scheduled intervals
    const stopData = [];
    stops.forEach((stop, idx) => {
      const actuals = (stop.variances || []).map(v => v * 60); // convert minutes to seconds
      const scheduledTime = stop.scheduledTime;
      
      // Convert scheduledTime to ms since midnight
      let [date, time] = scheduledTime.split(' ');
      let [h, m, s] = (time || scheduledTime).split(':').map(Number);
      const schedMs = (h * 3600 + m * 60 + (s || 0)) * 1000;
      
      // Calculate scheduled interval from previous stop
      const scheduledInterval = idx > 0 ? schedMs - stopData[idx - 1].schedMs : 0;
      
      // OTR window in seconds
      const otrWindow = [OTR_THRESHOLDS.early, OTR_THRESHOLDS.late];
      
      // Convert actuals to ms since midnight
      const actualTimes = actuals.map(sec => schedMs + sec * 1000);
      
      // Run all models
      const normal = normalModel(actualTimes, schedMs, otrWindow);
      const empirical = empiricalModel(actualTimes, schedMs, otrWindow);
      const logNormal = logNormalModel(actualTimes, schedMs, otrWindow);
      const weibull = weibullModel(actualTimes, schedMs, otrWindow);
      const robust = robustModel(actualTimes, schedMs, otrWindow);

      stopData.push({
        stop,
        schedMs,
        scheduledInterval,
        actualTimes,
        actuals,
        modelResults: {
          normal,
          empirical,
          logNormal,
          weibull,
          robust
        }
      });
    });

    // Second pass: Optimize times considering impact on subsequent stops
    let prevStopTime = null;
    const optimizedStops = stopData.map((data, idx) => {
      const { stop, schedMs, scheduledInterval, modelResults, actuals } = data;
      const { empirical } = modelResults;

      // Get the recommended time from empirical model
      let recommendedTime = empirical.recommendedTime;

      // If this isn't the last stop, consider impact on next stops
      if (idx < stopData.length - 1) {
        const nextStop = stopData[idx + 1];
        const nextEmpirical = nextStop.modelResults.empirical;
        
        // If next stop has high OTR, be more conservative with this stop's adjustment
        if (nextEmpirical.percentInWindow > 0.9) {
          // Weight the adjustment based on next stop's OTR
          const weight = 1 - (nextEmpirical.percentInWindow - 0.9) * 5; // Reduce adjustment if next stop is >90% OTR
          const rawShift = recommendedTime - schedMs;
          recommendedTime = schedMs + (rawShift * weight);
        }

        // Ensure we maintain at least 30s dwell time
        const minDwellTime = 30 * 1000; // 30 seconds in ms
        if (prevStopTime !== null) {
          const minTime = prevStopTime + minDwellTime;
          if (recommendedTime < minTime) {
            recommendedTime = minTime;
          }
        }
      }

      // Update previous stop time for next iteration
      prevStopTime = recommendedTime;

      // Convert recommended time back to HH:MM:SS format
      const totalSeconds = Math.round(recommendedTime / 1000);
      const ph = Math.floor(totalSeconds / 3600);
      const pm = Math.floor((totalSeconds % 3600) / 60);
      const ps = Math.floor(totalSeconds % 60);
      let proposedTime = `${ph.toString().padStart(2, '0')}:${pm.toString().padStart(2, '0')}:${ps.toString().padStart(2, '0')}`;
      if (stop.scheduledTime.includes(' ')) {
        proposedTime = `${stop.scheduledTime.split(' ')[0]} ${proposedTime}`;
      }

      return {
        ...stop,
        proposedTime,
        proposedTimeMs: recommendedTime,
        shiftSeconds: Math.round((recommendedTime - schedMs) / 1000),
        percentInWindow: empirical.percentInWindow * 100,
        actualsCount: actuals.length,
        currentOTR: stop.currentOTR,
        modelResults
      };
    });

    result.push(...optimizedStops);
  });
  
  return result;
}; 